import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { TextInput, Textarea, NumberInput, Switch, Button, Title, Stack, Group } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import VideoUpload from '@/components/VideoUpload'
import { api } from '@/lib/api'

export default function LessonForm() {
  const { courseId, id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)

  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      order: 1,
      duration: '0:30',
      isFree: false,
      isMemberOnly: false,
      videoUrl: '',
    },
  })

  useEffect(() => {
    if (id && courseId) {
      api.admin.getLessons(courseId).then((lessons) => {
        const lesson = lessons.find((l) => (l._id as string) === id)
        if (lesson) {
          form.setValues({
            title: (lesson.title as string) || '',
            description: (lesson.description as string) || '',
            order: (lesson.order as number) || 1,
            duration: (() => {
            const raw = lesson.duration as number | string
            if (typeof raw === 'number') {
              const mins = Math.floor(raw / 60)
              const secs = raw % 60
              return `${mins}:${secs.toString().padStart(2, '0')}`
            }
            return (raw as string) || '0:00'
          })(),
            isFree: (lesson.isFree as boolean) || false,
            isMemberOnly: (lesson.isMemberOnly as boolean) || false,
            videoUrl: (lesson.videoUrl as string) || '',
          })
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, courseId])

  const handleVideoFile = (file: File) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.src = url
    video.addEventListener('loadedmetadata', () => {
      const totalSecs = Math.floor(video.duration)
      const mins = Math.floor(totalSecs / 60)
      const secs = totalSecs % 60
      form.setFieldValue('duration', `${mins}:${secs.toString().padStart(2, '0')}`)
    })
  }
  const handleSubmit = async (values: typeof form.values) => {
    if (!courseId) return
    setLoading(true)
    try {
      const [mins, secs] = String(values.duration).split(':').map(Number)
      const durationAsNumber = (mins || 0) * 60 + (secs || 0)

      const payload = { ...values, duration: durationAsNumber }

      if (id) {
        await api.admin.updateLesson(id, payload)
        notifications.show({ title: 'Амжилттай', message: 'Хичээл шинэчлэгдлээ', color: 'green' })
      } else {
        await api.admin.createLesson(courseId, payload)
        notifications.show({ title: 'Амжилттай', message: 'Хичээл үүсгэгдлээ', color: 'green' })
      }
      navigate(`/courses/${courseId}/lessons`)
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Алдаа гарлаа', color: 'red' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <Title order={2} mb="lg">{id ? 'Хичээл засах' : 'Шинэ хичээл'}</Title>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack maw={600}>
          <TextInput label="Гарчиг" required {...form.getInputProps('title')} />
          <Textarea label="Тайлбар" autosize minRows={3} {...form.getInputProps('description')} />
          <Group grow>
            <NumberInput label="Дугаар" min={1} {...form.getInputProps('order')} />
            <TextInput label="Хугацаа (мин)" readOnly min={"2:30"} {...form.getInputProps('duration')} />
          </Group>
          <Switch label="Үнэгүй хичээл" {...form.getInputProps('isFree', { type: 'checkbox' })} />
          <Switch label="Зөвхөн гишүүдэд" {...form.getInputProps('isMemberOnly', { type: 'checkbox' })} />
          <div>
            <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 4, display: 'block' }}>Видео</label>
            <VideoUpload value={form.values.videoUrl} onChange={(url) => form.setFieldValue('videoUrl', url)} onFileSelected={handleVideoFile}/>
          </div>
          <Group>
            <Button type="submit" loading={loading}>{id ? 'Хадгалах' : 'Үүсгэх'}</Button>
            <Button variant="default" onClick={() => navigate(`/courses/${courseId}/lessons`)}>Буцах</Button>
          </Group>
        </Stack>
      </form>
    </div>
  )
}
